import { images } from '../data/images';
import { ownerDocuments, ownerImages, ownerProfile, ownerVehicles } from '../data/ownerProfile';
import { brtaMismatchFields, cngVehicleChecklist, mclVehicleChecklist, ownerChecklist, reviewRoles } from '../data/applicationReview';
import { brtaBadge, brtaOwnerOf, findDuplicate, isBlockingDuplicate } from './applicationQueue';
import { toBn } from './bn';
import type { Cng } from '../types/domain';
import type { OwnerApplication, OwnerRegistration } from '../types/owner';
import type { AppReview, BrtaRow, HistoryEntry, OwnerReviewInfo, ReviewDoc, ValidationItem, VehicleReviewInfo } from '../types/review';

export function nowStamp(): string {
  const d = new Date();
  return toBn(`13/10/2026 ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`);
}

const fathers = ['মোঃ আব্দুল জলিল', 'মোঃ নূরুল ইসলাম', 'মোঃ আবুল কাশেম', 'মোঃ মফিজুর রহমান'];
const mothers = ['মোছাঃ হালিমা খাতুন', 'মোছাঃ রাবেয়া বেগম', 'মোছাঃ জাহানারা বেগম', 'মোছাঃ সালমা খাতুন'];

export function ownerInfo(app: OwnerApplication, registrations: OwnerRegistration[]): OwnerReviewInfo {
  if (app.ownerId === ownerProfile.id) {
    return { name: ownerProfile.name, father: ownerProfile.father, mother: ownerProfile.mother, nid: ownerProfile.nid, dob: ownerProfile.dob, mobile: ownerProfile.mobile, address: 'মিরপুর-১০, ঢাকা', photo: ownerProfile.photo };
  }
  const reg = registrations.find((r) => r.id === app.ownerId);
  const seed = Number(app.id.slice(-1)) % 4;
  return {
    name: app.ownerName,
    father: fathers[seed],
    mother: mothers[seed],
    nid: reg?.nid ?? toBn(`19${app.ownerMobile.length}8${app.id.slice(-6)}2104`.slice(0, 13)),
    dob: reg?.dob ?? '১২/০৮/১৯৮৫',
    mobile: app.ownerMobile,
    address: `${app.division}, ঢাকা`,
    photo: reg?.photo
  };
}

export function vehicleInfo(app: OwnerApplication): VehicleReviewInfo {
  const known = ownerVehicles.find((v) => v.id === app.vehicleId);
  const cng = app.vehicleType === 'cng';
  const [brand, ...rest] = app.model.split(' ');
  return {
    typeLabel: cng ? 'সিএনজি অটোরিকশা' : 'মোটরসাইকেল',
    brand: known?.brand ?? brand,
    model: known?.model ?? rest.join(' '),
    color: known?.color ?? (cng ? 'সবুজ' : 'কালো'),
    year: known?.year ?? '২০২২',
    fuel: known?.fuel ?? (cng ? 'CNG' : 'পেট্রোল'),
    regDate: known?.regDate ?? '১৫/০৩/২০২২',
    fitnessExpiry: cng ? (app.vehicleId === 'c1' ? '৩০/০৬/২০২৭' : '৩১/০৩/২০২৭') : 'প্রযোজ্য নয়',
    taxExpiry: app.vehicleId === 'c1' ? '৩১/১২/২০২৬' : cng ? '৩০/০৬/২০২৭' : '২০/০২/২০২৭',
    routeExpiry: cng ? (app.vehicleId === 'c1' ? '১৫/১১/২০২৬' : '৩১/০১/২০২৭') : 'প্রযোজ্য নয়',
    image: known?.image ?? (cng ? images.cng : ownerImages.motorcycle)
  };
}

export function vehicleChecklist(app: OwnerApplication): string[] {
  return app.vehicleType === 'cng' ? cngVehicleChecklist : mclVehicleChecklist;
}

export function reviewDocs(app: OwnerApplication, nid: string): ReviewDoc[] {
  const v = vehicleInfo(app);
  const known = ownerDocuments[app.vehicleId ?? ''] ?? [];
  const pick = (key: string, fallback: Omit<ReviewDoc, 'key' | 'preview'>, preview = ownerImages.docScan): ReviewDoc => {
    const k = known.find((d) => d.key === key);
    return { key, preview, ...(k ? { name: k.name, number: k.number, issue: k.issue, expiry: k.expiry } : fallback) };
  };
  const sfx = app.id.slice(-4);
  const base = [
    pick('reg', { name: 'রেজিস্ট্রেশন সার্টিফিকেট', number: `BRTA-RC-DH-2022-${sfx}`, issue: v.regDate, expiry: 'প্রযোজ্য নয়' }),
    ...(app.vehicleType === 'cng'
      ? [
          pick('fitness', { name: 'ফিটনেস সার্টিফিকেট', number: `FIT-2025-DH-${sfx}`, issue: '০১/০৪/২০২৫', expiry: v.fitnessExpiry }),
          pick('tax', { name: 'ট্যাক্স টোকেন', number: `TAX-2026-00${sfx}`, issue: '০১/০৭/২০২৬', expiry: v.taxExpiry }),
          pick('route', { name: 'রুট পারমিট', number: `RP-DMP-CNG-2026-${sfx}`, issue: '০১/০২/২০২৬', expiry: v.routeExpiry })
        ]
      : [pick('tax', { name: 'ট্যাক্স টোকেন', number: `TAX-2026-02${sfx}`, issue: '২১/০২/২০২৬', expiry: v.taxExpiry })]),
    pick('owner', { name: 'মালিকানার প্রমাণপত্র', number: `SALE-DEED-2022-${sfx}`, issue: '১০/০৩/২০২২', expiry: 'প্রযোজ্য নয়' }),
    pick('photo', { name: 'গাড়ির ছবি', number: '৪টি ছবি (সামনে, পেছনে, দুই পাশ)', issue: app.submitted, expiry: 'প্রযোজ্য নয়' }, v.image),
    { key: 'nid', name: 'NID (মালিক)', number: nid, issue: 'নির্বাচন কমিশন', expiry: 'প্রযোজ্য নয়', preview: ownerImages.docScan }
  ];
  return base;
}

function tweak(value: string): string {
  const last = value.slice(-1);
  return value.slice(0, -1) + (last === '0' ? '1' : '0');
}

export function brtaRows(app: OwnerApplication, flags: string[]): BrtaRow[] {
  const v = vehicleInfo(app);
  const mm = app.brtaResult === 'অমিল' ? brtaMismatchFields[app.id] : undefined;
  const rows: { label: string; owner: string; brta: string }[] = [
    { label: 'রেজিস্ট্রেশন নম্বর', owner: app.vehicleReg, brta: app.vehicleReg },
    { label: 'মালিকের নাম', owner: app.ownerName, brta: brtaOwnerOf(app) },
    { label: 'চেসিস নম্বর', owner: app.chassis, brta: mm === 'চেসিস নম্বর' ? tweak(app.chassis) : app.chassis },
    { label: 'ইঞ্জিন নম্বর', owner: app.engine, brta: mm === 'ইঞ্জিন নম্বর' ? tweak(app.engine) : app.engine },
    { label: 'নিবন্ধনের তারিখ', owner: v.regDate, brta: v.regDate },
    ...(app.vehicleType === 'cng' ? [{ label: 'ফিটনেস মেয়াদ', owner: v.fitnessExpiry, brta: v.fitnessExpiry }] : []),
    { label: 'ট্যাক্স টোকেনের মেয়াদ', owner: v.taxExpiry, brta: v.taxExpiry }
  ];
  return rows.map((r) => ({ ...r, match: r.owner === r.brta && !flags.includes(r.label) }));
}

export function defaultReview(app: OwnerApplication, driverIds: string[], docKeys: string[]): AppReview {
  const done = app.stage >= 4 && app.status !== 'সংশোধন প্রয়োজন';
  const correction = app.status === 'সংশোধন প্রয়োজন';
  const last = app.times[app.times.length - 1] ?? app.submitted;
  return {
    assignedOfficer: app.stage >= 2 || correction ? 'মোঃ শফিকুল ইসলাম' : undefined,
    ownerChecks: ownerChecklist.map(() => done || correction),
    ownerResult: done || correction ? 'যাচাইকৃত' : undefined,
    vehicleChecks: vehicleChecklist(app).map(() => done),
    vehicleResult: done ? 'যাচাইকৃত' : correction && app.brtaResult === 'অমিল' ? 'তথ্যের অমিল' : undefined,
    docs: Object.fromEntries(docKeys.map((k) => [k, done || (correction && k !== 'reg') ? 'verified' : correction ? 'unclear' : 'pending'])),
    brtaDone: app.stage >= 3,
    brtaFlags: [],
    drivers: Object.fromEntries(driverIds.map((d) => [d, done ? 'approved' : 'pending'])),
    driverNotes: {},
    validation: {
      officer: 'মোঃ শফিকুল ইসলাম',
      date: '১৩/১০/২০২৬',
      observation: done ? 'মালিক, গাড়ি ও সকল ডকুমেন্ট BRTA রেকর্ডের সাথে সামঞ্জস্যপূর্ণ। কোনো ডুপ্লিকেট রেকর্ড পাওয়া যায়নি।' : '',
      comment: done ? 'অনুমোদনের সুপারিশ করা হলো।' : '',
      result: 'তথ্য সঠিক',
      saved: done
    },
    approval: app.stage >= 5 ? { officer: reviewRoles[2].user, designation: reviewRoles[2].designation, date: app.times[4] ?? last, comment: 'যাচাই সন্তোষজনক — অনুমোদিত।' } : undefined,
    rejection: app.status === 'বাতিল' ? { reason: 'অন্যান্য', detail: app.correctionReason ?? '', reference: '', by: reviewRoles[2].user, date: last } : undefined,
    corrections: correction && app.correctionReason
      ? [{ date: last, reasons: ['BRTA তথ্যের অমিল'], detail: app.correctionReason, instruction: app.correctionReason, comment: '', by: 'মোঃ শফিকুল ইসলাম' }]
      : []
  };
}

const historyLabels = ['আবেদন জমা হয়েছে', 'প্রাথমিক যাচাই শুরু', 'BRTA তথ্য যাচাই সম্পন্ন', 'যাচাই সম্পন্ন — অনুমোদনের জন্য পাঠানো হয়েছে', 'অনুমোদিত', 'QR স্টিকার তৈরি', 'স্টিকার ইস্যু'];

export function buildHistory(app: OwnerApplication): HistoryEntry[] {
  const users = [
    `${app.ownerName} (গাড়ির মালিক — মোবাইল অ্যাপ)`,
    'মোঃ রফিকুল ইসলাম (ডাটা রিভিউ অপারেটর)',
    'সিস্টেম — BRTA সিমুলেশন',
    'মোঃ শফিকুল ইসলাম (যাচাই কর্মকর্তা)',
    'মোঃ কামরুল হাসান (অনুমোদনকারী কর্মকর্তা)',
    'মোঃ সাজ্জাদ হোসেন (স্টিকার অপারেটর)',
    'ট্রাফিক বিভাগ, ডিএমপি'
  ];
  const n = Math.max(1, Math.min(app.stage, 7));
  const out: HistoryEntry[] = Array.from({ length: n }).map((_, i) => ({
    time: app.times[i] ?? app.submitted,
    user: users[i],
    action: historyLabels[i],
    status: 'সম্পন্ন',
    tone: 'green',
    remarks: i === 2 ? `ফলাফল: ${brtaBadge(app).label}` : i === 0 ? 'উৎস: গাড়ির মালিকের মোবাইল অ্যাপ' : undefined
  }));
  const last = app.times[app.times.length - 1] ?? app.submitted;
  if (app.status === 'সংশোধন প্রয়োজন') out.push({ time: last, user: users[3], action: 'সংশোধনের জন্য ফেরত', status: 'সংশোধন প্রয়োজন', tone: 'orange', remarks: app.correctionReason });
  if (app.status === 'বাতিল') out.push({ time: last, user: users[4], action: 'আবেদন বাতিল', status: 'বাতিল', tone: 'red', remarks: app.correctionReason });
  return out;
}

export function validationItems(app: OwnerApplication, r: AppReview, apps: OwnerApplication[], driverIds: string[]): ValidationItem[] {
  const docsAll = Object.values(r.docs).every((s) => s === 'verified');
  const brtaOk = r.brtaDone && brtaRows(app, r.brtaFlags).every((x) => x.match);
  const dup = findDuplicate(app, apps);
  const blocking = isBlockingDuplicate(app, apps);
  const drvDone = app.vehicleType !== 'cng' || driverIds.every((d) => (r.drivers[d] ?? 'pending') !== 'pending');
  return [
    { label: 'মালিকের তথ্য যাচাই', done: r.ownerResult === 'যাচাইকৃত', tab: 'owner' },
    { label: 'NID ডকুমেন্ট যাচাই', done: r.docs.nid === 'verified', tab: 'docs' },
    { label: 'গাড়ির তথ্য যাচাই', done: r.vehicleResult === 'যাচাইকৃত', tab: 'vehicle' },
    { label: 'BRTA তথ্য যাচাই', done: brtaOk, tab: 'brta' },
    { label: 'প্রয়োজনীয় ডকুমেন্ট যাচাই', done: docsAll, tab: 'docs' },
    { label: 'ডুপ্লিকেট গাড়ি যাচাই', done: !blocking, tab: 'summary', note: dup ? (blocking ? `অমীমাংসিত ডুপ্লিকেট: ${dup.id}` : `অপর আবেদন ${dup.id} — BRTA মালিক এই আবেদনকারী`) : 'কোনো ডুপ্লিকেট নেই' },
    { label: 'চালকের তথ্য যাচাই (প্রযোজ্য হলে)', done: drvDone, tab: 'drivers', note: app.vehicleType === 'cng' ? undefined : 'মোটরসাইকেলের জন্য প্রযোজ্য নয়' }
  ];
}

export function buildCngRecord(app: OwnerApplication, id: string, qrId: string, owner: OwnerReviewInfo): Cng {
  const v = vehicleInfo(app);
  return {
    id,
    reg: app.vehicleReg,
    type: v.typeLabel,
    chassis: app.chassis,
    engine: app.engine,
    model: app.model,
    color: v.color,
    year: v.year,
    brtaRegDate: v.regDate,
    fitness: app.vehicleType === 'cng' ? 'বৈধ' : 'প্রযোজ্য নয়',
    fitnessExpiry: app.vehicleType === 'cng' ? v.fitnessExpiry : '—',
    taxToken: 'বৈধ',
    taxExpiry: v.taxExpiry,
    routePermit: app.vehicleType === 'cng' ? 'বৈধ' : 'প্রযোজ্য নয়',
    qrId,
    owner: {
      name: owner.name,
      father: owner.father,
      nid: owner.nid,
      nidMasked: `${owner.nid.slice(0, 4)}*****${owner.nid.slice(-3)}`,
      mobile: owner.mobile,
      mobileMasked: `${owner.mobile.slice(0, 5)}****${owner.mobile.slice(-2)}`,
      address: owner.address,
      status: 'যাচাইকৃত',
      photo: owner.photo
    },
    driverIds: [],
    brtaStatus: 'যাচাইকৃত',
    division: app.division,
    stage: 4,
    source: 'মালিক স্ব-নিবন্ধন',
    approvedOn: '১৩ অক্টোবর ২০২৬'
  };
}
